// src/components/layout/Dashboard/Restaurant/Restaurant.jsx
import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../../../hooks/usePermissions";
import PermissionGuard from "../../../../components/RBAC/PermissionGuard";
import RestaurantDetailPage from "./RestaurantDetail";
import ExportRestaurantModal from "./ExportRestaurantModal";
import { getData, deleteData, serverURL } from "../../../../services/Fetchnodeservices";
import { ThemeContext } from "../../../../context/ThemeContext";

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT LIBRARIES - Install: npm install jspdf jspdf-autotable xlsx file-saver
// ─────────────────────────────────────────────────────────────────────────────
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ─────────────────────────────────────────────────────────────────────────────
// API CONFIG
// ─────────────────────────────────────────────────────────────────────────────
let restaurantSourceRequest = null;

const fetchRestaurantSource = async () => {
  if (restaurantSourceRequest) return restaurantSourceRequest;

  restaurantSourceRequest = getData("restaurant/Restrunts");
  try {
    return await restaurantSourceRequest;
  } finally {
    restaurantSourceRequest = null;
  }
};

const fetchResto = async ({ page, limit, search, status, ready, onOff, approveStatus, locationFilter, showHidden = false }) => {
  try {
    const response = await fetchRestaurantSource();
    const data = response;
    
    let restaurantsList = Array.isArray(data) ? data : (data.data || data.items || []);
    let filtered = [...restaurantsList];

    if (!showHidden) {
      filtered = filtered.filter((restaurant) => String(restaurant.hide || '0') !== '1');
    }
    
    if (search) {
      const searchText = search.trim().toLowerCase();
      filtered = filtered.filter((r) => {
        // Address fields are included so an admin can search an area, city,
        // landmark, pincode, or any part of the saved restaurant address.
        const searchableFields = [
          r.name, r.organisation_name, r.owner_mobile, r.contact, r.id,
          r.area, r.locality, r.address, r.city, r.state, r.location,
          r.landmark, r.postal
        ];
        return searchableFields.some((field) =>
          String(field ?? "").toLowerCase().includes(searchText)
        );
      });
    }
    if (status && status !== "All") {
      filtered = filtered.filter(r => {
        const itemStatus = r.status === "1" ? "Active" : "Inactive";
        return itemStatus === status;
      });
    }
    if (ready && ready !== "All") {
      filtered = filtered.filter(r => {
        const itemReady = r.open_status === "1" ? "Yes" : "No";
        return itemReady === ready;
      });
    }
    if (onOff && onOff !== "All") {
      filtered = filtered.filter(r => {
        const itemOnOff = r.open_status === "1" ? "ON" : "OFF";
        return itemOnOff === onOff;
      });
    }
    if (approveStatus && approveStatus !== "All") {
      filtered = filtered.filter(r => {
        const itemApprove = String(r.is_approved) === "1" ? "Approved" : (String(r.is_approved) === "2" ? "Rejected" : "Pending");
        return itemApprove === approveStatus;
      });
    }
    if (locationFilter && locationFilter !== "All") {
      filtered = filtered.filter(r => {
        const restaurantCity = r.city || r.address?.split(',').pop()?.trim() || "Unknown";
        return restaurantCity.trim().toLowerCase() === locationFilter.trim().toLowerCase();
      });
    }
    
    const start = (page - 1) * limit;
    const paginated = filtered.slice(start, start + limit);
    
    const mappedData = paginated.map(item => {
      return {
        rawData: item,
        id: item.id,
        restroId: item.id,
        old_id: item.old_id || item.id || "",
        name: item.name || "",
        organisation_name: item.organisation_name || item.name || "",
        owner_name: item.owner_name || "",
        owner_mobile: item.owner_mobile || "",
        contact: item.contact || item.owner_mobile || "",
        email: item.email || "",
        password: item.password || "",
        address: item.address || "",
        city: item.city || item.address?.split(',').pop()?.trim() || "Unknown",
        area: item.area || item.locality || "",
        state: item.state || "",
        postal: item.postal || "",
        location: item.location || "",
        latitude: item.latitude || null,
        longitude: item.longitude || null,
        meet_to: item.meet_to || "",
        interestedin_zaptodoor: item.interestedin || "",
        interestedin: item.interestedin || "",
        other_person_name: item.other_person_name || "",
        other_person_mobile: item.other_person_mobile || "",
        pic: item.pic || item.r_logo || null,
        restropic: item.pic || item.r_logo || null,
        r_logo: item.r_logo || null,
        h_logo: item.h_logo || null,
        status: String(item.status) === "1" ? "Active" : "Inactive",
        ready: String(item.open_status) === "1" ? "Yes" : "No",
        onOff: String(item.open_status) === "1" ? "ON" : "OFF",
        approveStatus: String(item.is_approved) === "1" ? "Approved" : (String(item.is_approved) === "2" ? "Rejected" : "Pending"),
        is_approved: item.is_approved || "0",
        hide: item.hide || "0",
        open_status: item.open_status || "0",
        is_ready: item.is_ready || "0",
        shiftStart: item.shift_start || "10:00 AM",
        shiftEnd: item.shift_end || "10:00 PM",
        shift_start: item.shift_start || "10:00 AM",
        shift_end: item.shift_end || "10:00 PM",
        is_auto_schedule: item.is_auto_schedule || "0",
        is_schedule_approved: item.is_schedule_approved || "0",
        rating: parseFloat(item.rating) || 4.0,
        totalRatings: parseInt(item.total_ratings) || 0,
        totalReviews: parseInt(item.total_reviews) || 0,
        totalPayments: parseFloat(item.platform_charge) || 0,
        pendingPayments: 0,
        completedPayments: 0,
        platform_charge: parseFloat(item.platform_charge) || 0,
        handling_charge: parseFloat(item.handling_charge) || 0,
        gst_percentage: parseFloat(item.gst_percentage) || 0,
        platform_share: parseFloat(item.comission) || 0,
        pay_perclick: 0,
        wallet: parseFloat(item.wallet) || 0,
        paid: item.paid === "1" ? "1" : "0",
        bank_name: item.bank_name || "",
        bank_branch: item.bank_branch || "",
        bank_type: item.bank_type || "",
        ifsc_code: item.ifsc || "",
        ifsc: item.ifsc || "",
        account_holder: item.account_holder || "",
        account_number: item.account_number || "",
        bank_mobile: item.bank_mobile || "",
        upi: item.upi || "",
        bank_pass: item.bank_pass || null,
        cancel_cheque: item.cancel_cheque || null,
        gst_number: item.gst_number || "",
        gst_pic: item.gst_pic || null,
        food_license_number: item.food_license_number || "",
        food_license_pic: item.food_license_pic || null,
        aadhar_front: item.aadhar_front || null,
        aadhar_back: item.aadhar_back || null,
        pan_card: item.pan_card || null,
        aadhar_number: item.aadhar_number || "",
        pan_number: item.pan_number || "",
        is_verify_personalinfo: item.is_verify_personalinfo || "0",
        is_verify_docinfo: item.is_verify_docinfo || "0",
        is_verify_bankinfo: item.is_verify_bankinfo || "0",
        remarks_personalinfo: item.remarks_personalinfo || "",
        remarks_docinfo: item.remarks_docinfo || "",
        remarks_bankinfo: item.remarks_bankinfo || "",
        is_approved: item.is_approved || "0",
        doc_verification: item.doc_verification || null,
        is_menu: item.is_menu || "0",
        isKOT: item.isKOT || "0",
        isBill: item.isBill || "0",
        menu_direction: item.menu_direction || "neutral",
        menu_direction_type: item.menu_direction_type || "percentage",
        menu_direction_value: item.menu_direction_value || "",
        current_menu_page1: item.current_menu_page1 || null,
        current_menu_page2: item.current_menu_page2 || null,
        current_menu_page3: item.current_menu_page3 || null,
        current_menu_page4: item.current_menu_page4 || null,
        current_menu_page5: item.current_menu_page5 || null,
        current_menu_page6: item.current_menu_page6 || null,
        current_menu_page7: item.current_menu_page7 || null,
        current_menu_page8: item.current_menu_page8 || null,
        current_menu_page9: item.current_menu_page9 || null,
        cuisine: item.cuisine || "",
        description: item.details || "",
        details: item.details || "",
        comission: item.comission || "",
        special_comission: item.special_comission || "",
        min_order: item.min_order || "",
        order_receiving_charges: item.order_receiving_charges || "",
        subscription_id: item.subscription_id || "",
        is_mou: item.is_mou || "0",
        myreferal: item.myreferal || "",
        refer_by: item.refer_by || "",
        created_by_id: item.created_by_id || "1",
        phn_otp1: item.contact || "",
        phn_otp2: "",
        login_session: item.login_session || "",
        refer_code: item.myreferal || "",
        created_at: item.created_at,
        updated_at: item.updated_at,
        menu: [],
        schedule: [
          { day: "Monday", openTime: "09:00", closeTime: "23:00", isClosed: false },
          { day: "Tuesday", openTime: "09:00", closeTime: "23:00", isClosed: false },
          { day: "Wednesday", openTime: "09:00", closeTime: "23:00", isClosed: false },
          { day: "Thursday", openTime: "09:00", closeTime: "23:00", isClosed: false },
          { day: "Friday", openTime: "09:00", closeTime: "23:00", isClosed: false },
          { day: "Saturday", openTime: "10:00", closeTime: "00:00", isClosed: false },
          { day: "Sunday", openTime: "10:00", closeTime: "22:00", isClosed: false }
        ]
      };
    });
    
    return { data: mappedData, total: filtered.length, page, limit };
  } catch (error) {
    console.error("❌ Error fetching restaurants:", error);
    return { data: [], total: 0, page, limit };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const TrashIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);
const SearchIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);
const ChevronLeftIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>);
const ChevronRightIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>);
const PlusIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const PhoneIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>);
const PDFIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>);
const ExcelIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="16" x2="16" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="8" x2="10" y2="8"/></svg>);
const DownloadIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>);
const ChevronDownIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>);
const FileSpreadsheetIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="16" x2="16" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="8" x2="10" y2="8"/></svg>);

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────

const RestaurantAvatar = ({ pic, name, theme }) => {
  const [imageError, setImageError] = useState(false);
  const getCleanFilename = (picPath) => {
    if (!picPath || picPath.trim() === "") return null;
    if (picPath.includes('/')) return picPath.split('/').pop();
    return picPath;
  };
  const filename = getCleanFilename(pic);
  const imageUrl = filename ? `${serverURL}/public/uploadnew/upload/${filename}` : null;
  
  if (!imageUrl || imageError) {
    const initial = name?.charAt(0) || "🍔";
    return (
      <div style={{
        width: "32px", height: "32px", borderRadius: "50%",
        background: theme.primaryGradient,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "14px", fontWeight: 700, color: "#fff",
      }}>
        {initial}
      </div>
    );
  }
  return (
    <img src={imageUrl} alt={name} onError={() => setImageError(true)} style={{
      width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover", border: `2px solid ${theme.border}`
    }} />
  );
};

const StatusBadge = ({ status, theme }) => {
  const config = {
    Active: { bg: `${theme.success}1a`, color: theme.success, dot: theme.success },
    Inactive: { bg: `${theme.textMuted}1a`, color: theme.textMuted, dot: theme.textMuted },
  };
  const c = config[status] || config.Inactive;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 12px 4px 8px", borderRadius: "20px", background: c.bg, border: `1px solid ${c.bg}` }}>
      <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: c.dot }} />
      <span style={{ fontSize: "12px", fontWeight: 600, color: c.color }}>{status}</span>
    </div>
  );
};

const ReadyBadge = ({ ready, theme }) => {
  const config = {
    Yes: { bg: `${theme.success}1a`, color: theme.success },
    No: { bg: `${theme.warning}1a`, color: theme.warning },
  };
  const c = config[ready] || config.No;
  return <span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600, background: c.bg, color: c.color }}>{ready}</span>;
};

const OnOffBadge = ({ value, theme }) => {
  const config = {
    ON: { bg: `${theme.success}1a`, color: theme.success },
    OFF: { bg: `${theme.textMuted}1a`, color: theme.textMuted },
  };
  const c = config[value] || config.OFF;
  return <span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 700, background: c.bg, color: c.color }}>{value}</span>;
};

const ApproveStatusBadge = ({ isApproved, theme }) => {
  const approved = Number(isApproved) === 1;
  const color = approved ? theme.success : theme.warning;
  const label = approved ? "Approved" : "Pending";

  return (
    <span
      title={label}
      aria-label={label}
      style={{
        display: "inline-block",
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        background: `${color}1a`,
        position: "relative",
      }}
    >
      <span style={{
        position: "absolute",
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: color,
        top: "6px",
        left: "6px",
      }} />
    </span>
  );
};

const Checkbox = ({ checked, onChange, theme }) => (
  <div onClick={onChange} style={{
    width: "16px", height: "16px", borderRadius: "4px",
    border: checked ? `2px solid ${theme.primary}` : `2px solid ${theme.border}`,
    background: checked ? theme.primary : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", transition: "all 0.15s ease",
  }}>
    {checked && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>}
  </div>
);

const Toast = ({ message, type, onClose, theme }) => {
  const bg = type === "error" ? `${theme.error}1a` : `${theme.success}1a`;
  const border = type === "error" ? `${theme.error}40` : `${theme.success}40`;
  const textColor = type === "error" ? theme.error : theme.success;
  const dotColor = type === "error" ? theme.error : theme.success;
  return (
    <div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 999, background: theme.surface, border: `1px solid ${border}`, borderRadius: "10px", padding: "12px 18px", display: "flex", alignItems: "center", gap: "10px", boxShadow: `0 8px 32px ${theme.primaryLight}`, animation: "slideUp 0.25s ease" }}>
      <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: dotColor }} />
      <span style={{ fontSize: "13px", color: textColor, flex: 1 }}>{message}</span>
      <span onClick={onClose} style={{ cursor: "pointer", color: theme.textMuted, fontSize: "16px" }}>×</span>
    </div>
  );
};

const DeleteConfirmModal = ({ isOpen, restaurantName, onConfirm, onCancel, deleting, theme }) => {
  if (!isOpen) return null;
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, animation: "fadeIn 0.2s ease" }}>
      <div style={{ background: theme.surface, borderRadius: "12px", padding: "24px", maxWidth: "400px", width: "90%", border: `1px solid ${theme.border}` }}>
        <h3 style={{ color: theme.textPrimary, marginBottom: "8px", fontSize: "18px" }}>Confirm Delete</h3>
        <p style={{ color: theme.textMuted, marginBottom: "20px", fontSize: "14px" }}>Are you sure you want to delete <strong>{restaurantName}</strong>?</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button disabled={deleting} onClick={onCancel} style={{ padding: "8px 16px", borderRadius: "6px", border: `1px solid ${theme.border}`, background: "transparent", color: theme.textSecondary, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.6 : 1, fontSize: "13px" }}>Cancel</button>
          <button disabled={deleting} onClick={onConfirm} style={{ padding: "8px 16px", borderRadius: "6px", border: "none", background: theme.error, color: "white", cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.7 : 1, fontSize: "13px" }}>{deleting ? "Deleting..." : "Delete"}</button>
        </div>
      </div>
    </div>
  );
};

const RestaurantMenuModal = ({ restaurant, onClose, theme }) => (
  <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
    <div style={{ background: theme.surface, borderRadius: "12px", padding: "24px", width: "90%", maxWidth: "600px", maxHeight: "80vh", overflow: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
        <h3 style={{ color: theme.textPrimary, fontSize: "18px" }}>Menu - {restaurant.name}</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", color: theme.textMuted, fontSize: "20px", cursor: "pointer" }}>×</button>
      </div>
      <p style={{ textAlign: "center", color: theme.textMuted, padding: "40px", fontSize: "14px" }}>Menu management coming soon</p>
    </div>
  </div>
);

const ScheduleModal = ({ isOpen, restaurant, scheduleItems, onUpdate, onDelete, onAdd, onClose, onSave, theme }) => {
  const [newItem, setNewItem] = useState({ day: "Monday", openTime: "09:00", closeTime: "17:00", isClosed: false });
  if (!isOpen) return null;
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: theme.surface, borderRadius: "12px", padding: "24px", width: "90%", maxWidth: "800px", maxHeight: "80vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <h3 style={{ color: theme.textPrimary, fontSize: "18px" }}>Schedule - {restaurant?.name}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: theme.textMuted, fontSize: "20px", cursor: "pointer" }}>×</button>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <h4 style={{ color: theme.textPrimary, fontSize: "14px", marginBottom: "10px" }}>Add New Schedule</h4>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
            <select value={newItem.day} onChange={(e) => setNewItem({ ...newItem, day: e.target.value })} style={{ padding: "6px 10px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "6px", color: theme.textPrimary, fontSize: "13px" }}>
              {days.map(day => <option key={day}>{day}</option>)}
            </select>
            <input type="time" value={newItem.openTime} onChange={(e) => setNewItem({ ...newItem, openTime: e.target.value })} style={{ padding: "6px 10px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "6px", color: theme.textPrimary, fontSize: "13px" }} />
            <input type="time" value={newItem.closeTime} onChange={(e) => setNewItem({ ...newItem, closeTime: e.target.value })} style={{ padding: "6px 10px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "6px", color: theme.textPrimary, fontSize: "13px" }} />
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: theme.textSecondary }}>
              <input type="checkbox" checked={newItem.isClosed} onChange={(e) => setNewItem({ ...newItem, isClosed: e.target.checked })} /> Closed
            </label>
            <button onClick={() => {
              if (scheduleItems.some(item => item.day === newItem.day)) {
                alert("Schedule for this day already exists");
                return;
              }
              onAdd(newItem);
              setNewItem({ day: "Monday", openTime: "09:00", closeTime: "17:00", isClosed: false });
            }} style={{ padding: "6px 14px", background: theme.primary, border: "none", borderRadius: "6px", color: "white", cursor: "pointer", fontSize: "13px" }}>Add</button>
          </div>
        </div>
        <div>
          <h4 style={{ color: theme.textPrimary, fontSize: "14px", marginBottom: "10px" }}>Current Schedule</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {scheduleItems.map((item, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "8px", border: `1px solid ${theme.border}`, borderRadius: "6px", flexWrap: "wrap" }}>
                <span style={{ width: "100px", fontWeight: 600, color: theme.textPrimary, fontSize: "13px" }}>{item.day}</span>
                <input type="time" value={item.openTime} onChange={(e) => onUpdate(idx, { ...item, openTime: e.target.value })} style={{ padding: "5px 8px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "4px", fontSize: "12px", color: theme.textPrimary }} />
                <span style={{ fontSize: "12px", color: theme.textMuted }}>-</span>
                <input type="time" value={item.closeTime} onChange={(e) => onUpdate(idx, { ...item, closeTime: e.target.value })} style={{ padding: "5px 8px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "4px", fontSize: "12px", color: theme.textPrimary }} />
                <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px", color: theme.textSecondary }}>
                  <input type="checkbox" checked={item.isClosed} onChange={(e) => onUpdate(idx, { ...item, isClosed: e.target.checked })} /> Closed
                </label>
                <button onClick={() => onDelete(idx)} style={{ marginLeft: "auto", background: "none", border: "none", color: theme.error, cursor: "pointer", fontSize: "12px" }}>Delete</button>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "16px" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: "6px", color: theme.textSecondary, cursor: "pointer", fontSize: "13px" }}>Cancel</button>
          <button onClick={onSave} style={{ padding: "8px 16px", background: theme.primary, border: "none", borderRadius: "6px", color: "white", cursor: "pointer", fontSize: "13px" }}>Save Schedule</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DROPDOWN MENU COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const DropdownMenu = ({ trigger, children, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", display: "inline-block" }}>
      <div onClick={() => setIsOpen(!isOpen)} style={{ cursor: "pointer" }}>
        {trigger}
      </div>
      {isOpen && (
        <div style={{
          position: "absolute",
          top: "100%",
          right: 0,
          marginTop: "4px",
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: "8px",
          boxShadow: `0 8px 32px ${theme.primaryLight}`,
          minWidth: "200px",
          zIndex: 100,
          overflow: "hidden"
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

const DropdownItem = ({ onClick, children, theme, icon }) => (
  <div
    onClick={onClick}
    style={{
      padding: "10px 16px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "13px",
      color: theme.textPrimary,
      cursor: "pointer",
      transition: "all 0.15s ease",
      fontFamily: "'DM Sans', sans-serif"
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = theme.primaryLight;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
    }}
  >
    {icon && <span style={{ color: theme.primary }}>{icon}</span>}
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT - RestaurantTable
// ─────────────────────────────────────────────────────────────────────────────
const RestaurantTable = ({ isDark = true }) => {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  
  const [restaurants, setRestaurants] = useState([]);
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [readyFilter, setReadyFilter] = useState("All");
  const [onOffFilter, setOnOffFilter] = useState("All");
  const [approveStatusFilter, setApproveStatusFilter] = useState("All");
  const [locationFilter, setLocationFilter] = useState("All");
  const [showHidden, setShowHidden] = useState(false);
  const [uniqueLocations, setUniqueLocations] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [toast, setToast] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDetailPage, setShowDetailPage] = useState(null);
  const [showMenuModal, setShowMenuModal] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [scheduleRestaurant, setScheduleRestaurant] = useState(null);
  const [locationStats, setLocationStats] = useState({});
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const allRestaurantsFetchRef = useRef({ loading: false, loaded: false, data: [] });
  const restaurantListRequestRef = useRef(null);

  const showToast = (message, type = "success") => { 
    setToast({ message, type }); 
    setTimeout(() => setToast(null), 3500); 
  };
  const { hasPermission, permissions, role, loading } = usePermissions();
  
  const canExport = true;

  // ─── FETCH ALL RESTAURANTS ───
  const fetchAllRestaurants = useCallback(async ({ force = false } = {}) => {
    const fetchState = allRestaurantsFetchRef.current;

    if (fetchState.loading) {
      await fetchState.promise;
      return fetchState.data;
    }
    if (fetchState.loaded && !force) return fetchState.data;

    fetchState.loading = true;
    try {
      fetchState.promise = fetchResto({
        page: 1,
        limit: 10000,
        search: "",
        status: "All",
        ready: "All",
        onOff: "All",
        approveStatus: "All",
        locationFilter: "All",
        showHidden: false
      });

      const allRes = await fetchState.promise;
      const allData = allRes.data || [];
      fetchState.data = allData;
      fetchState.loaded = true;
      setAllRestaurants(allData);
      
      const locations = new Set(["All"]);
      allData.forEach((restaurant) => {
        const city = restaurant.city || restaurant.address?.split(',').pop()?.trim();
        if (city && city !== "Unknown") locations.add(city);
      });
      const uniqueLocs = Array.from(locations);
      setUniqueLocations(uniqueLocs);
      
      const stats = {};
      allData.forEach((restaurant) => {
        const city = restaurant.city || restaurant.address?.split(',').pop()?.trim() || "Unknown";
        const area = restaurant.area || restaurant.locality || "";
        const locationKey = area || city;
        if (locationKey && locationKey !== "Unknown") {
          stats[locationKey] = (stats[locationKey] || 0) + 1;
        }
      });
      setLocationStats(stats);
      return allData;
    } catch (err) {
      console.error("Error fetching all restaurants:", err);
      return [];
    } finally {
      fetchState.loading = false;
      fetchState.promise = null;
    }
  }, []);

  // ─── EXTRACT UNIQUE LOCATIONS ───
  // ─── CALCULATE LOCATION STATS ───
  // ─── FETCH RESTAURANTS ───
  const fetchRestaurants = useCallback(async () => {
    const requestParams = {
      page,
      limit: perPage,
      search: debouncedSearch,
      status: statusFilter,
      ready: readyFilter,
      onOff: onOffFilter,
      approveStatus: approveStatusFilter,
      locationFilter,
      showHidden
    };
    const requestKey = JSON.stringify(requestParams);
    const activeRequest = restaurantListRequestRef.current;

    if (activeRequest?.key === requestKey) return activeRequest.promise;

    const request = fetchResto(requestParams);
    restaurantListRequestRef.current = { key: requestKey, promise: request };

    try {
      const res = await request;
      setRestaurants(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      showToast(`Failed to load: ${err.message}`, "error");
    } finally {
      if (restaurantListRequestRef.current?.promise === request) {
        restaurantListRequestRef.current = null;
      }
    }
  }, [page, perPage, debouncedSearch, statusFilter, readyFilter, onOffFilter, approveStatusFilter, locationFilter, showHidden]);

  useEffect(() => { 
    fetchRestaurants(); 
  }, [fetchRestaurants]);

  useEffect(() => {
    fetchAllRestaurants();
  }, [fetchAllRestaurants]);

  useEffect(() => { 
    const t = setTimeout(() => { 
      setDebouncedSearch(search); 
      setPage(1); 
    }, 500); 
    return () => clearTimeout(t); 
  }, [search]);

  const handlePerPageChange = (event) => {
    setPerPage(Number(event.target.value));
    setPage(1);
  };

  // ─── GET EXPORT DATA ───
  const getExportData = (dataToExport) => {
    if (!dataToExport || dataToExport.length === 0) return [];
    return dataToExport.map(r => ({
      'Restaurant ID': r.restroId || r.id || '',
      'Name': r.organisation_name || r.name || 'N/A',
      'Owner': r.owner_name || 'N/A',
      'Contact': r.contact || 'N/A',
      'Email': r.email || 'N/A',
      'Status': r.status || 'N/A',
      'Ready': r.ready || 'N/A',
      'Approve Status': r.approveStatus || 'N/A',
      'ON/OFF': r.onOff || 'N/A',
      'Rating': r.rating || 'N/A',
      'City': r.city || 'N/A',
      'Area': r.area || 'N/A',
      'Address': r.address || 'N/A',
      'Cuisine': r.cuisine || 'N/A',
      'Shift Start': r.shiftStart || 'N/A',
      'Shift End': r.shiftEnd || 'N/A',
      'Platform Charge': r.platform_charge || '0',
      'GST %': r.gst_percentage || '0',
      'Wallet': r.wallet || '0',
      'Bank Name': r.bank_name || 'N/A',
      'Account Holder': r.account_holder || 'N/A',
      'IFSC': r.ifsc || 'N/A',
      'GST Number': r.gst_number || 'N/A',
      'Created At': r.created_at ? new Date(r.created_at).toLocaleString() : 'N/A'
    }));
  };

  // ─── EXPORT HANDLER (CSV & PDF) ───
  const handleExport = async ({ fields, dateRange, customDate, format }) => {
    setExporting(true);
    try {
      let dataToExport = allRestaurants.length > 0 ? allRestaurants : restaurants;
      
      if (dataToExport.length === 0) {
        showToast("Loading all restaurants...", "info");
        dataToExport = await fetchAllRestaurants();
      }
      
      if (dataToExport.length === 0) {
        showToast("No restaurants available to export!", "error");
        setExporting(false);
        return;
      }
      
      // Apply date filter
      if (dateRange !== 'all') {
        const now = new Date();
        let filterDate = new Date();
        
        switch(dateRange) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'this_week':
            filterDate.setDate(now.getDate() - now.getDay());
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'prev_week':
            filterDate.setDate(now.getDate() - now.getDay() - 7);
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'this_month':
            filterDate.setDate(1);
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'prev_month':
            filterDate.setMonth(now.getMonth() - 1);
            filterDate.setDate(1);
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'custom':
            if (customDate.from && customDate.to) {
              const from = new Date(customDate.from);
              const to = new Date(customDate.to);
              to.setHours(23, 59, 59, 999);
              dataToExport = dataToExport.filter(r => {
                const created = new Date(r.created_at);
                return created >= from && created <= to;
              });
            }
            break;
          default:
            break;
        }
        
        if (dateRange !== 'custom' && dateRange !== 'all') {
          dataToExport = dataToExport.filter(r => {
            const created = new Date(r.created_at);
            return created >= filterDate;
          });
        }
      }
      
      if (dataToExport.length === 0) {
        showToast("No restaurants match the selected date range!", "error");
        setExporting(false);
        return;
      }

      // Field mapping
      const fieldMap = {
        'id': 'id',
        'restaurant_name': 'organisation_name',
        'contact_person_name': 'owner_name',
        'prefix': 'prefix',
        'gender': 'gender',
        'contact': 'contact',
        'email': 'email',
        'password': 'password',
        'org_type': 'org_type',
        'address': 'address',
        'city': 'city',
        'state': 'state',
        'pin_code': 'postal',
        'latitude': 'latitude',
        'longitude': 'longitude',
        'owner_name': 'owner_name',
        'owner_mobile': 'owner_mobile',
        'other_person_name': 'other_person_name',
        'other_person_mobile': 'other_person_mobile',
        'banner_image': 'pic',
        'round_logo': 'r_logo',
        'horizontal_logo': 'h_logo',
        'aadhar_front': 'aadhar_front',
        'aadhar_back': 'aadhar_back',
        'gst_number': 'gst_number',
        'food_license_image': 'food_license_pic',
        'bank_name': 'bank_name',
        'bank_branch': 'bank_branch',
        'bank_type': 'bank_type',
        'account_holder': 'account_holder',
        'account_number': 'account_number',
        'ifsc': 'ifsc',
        'bank_mobile': 'bank_mobile',
        'upi': 'upi',
        'bank_passbook': 'bank_pass',
        'cancel_cheque': 'cancel_cheque',
        'commission': 'comission',
        'special_commission': 'special_comission',
        'platform_charge': 'platform_charge',
        'handling_charge': 'handling_charge',
        'gst_percentage': 'gst_percentage',
        'order_receiving_charges': 'order_receiving_charges',
        'min_order': 'min_order',
        'wallet': 'wallet',
        'paid_status': 'paid',
        'mou': 'is_mou',
        'subscription_id': 'subscription_id',
        'referral_code': 'myreferal',
        'referred_by': 'refer_by',
        'status': 'status',
        'open_status': 'open_status',
        'is_ready': 'is_ready',
        'schedule_mode': 'is_auto_schedule',
        'schedule_approved': 'is_schedule_approved',
        'shift_start': 'shift_start',
        'shift_end': 'shift_end',
        'personal_info_verified': 'is_verify_personalinfo',
        'documents_verified': 'is_verify_docinfo',
        'bank_verified': 'is_verify_bankinfo',
        'remarks_personal': 'remarks_personalinfo',
        'remarks_documents': 'remarks_docinfo',
        'remarks_bank': 'remarks_bankinfo',
        'created_at': 'created_at',
        'updated_at': 'updated_at',
        'remarks': 'remarks'
      };

      const exportData = dataToExport.map(r => {
        const row = {};
        fields.forEach(field => {
          const key = fieldMap[field];
          if (key && r[key] !== undefined) {
            row[field] = r[key] !== null && r[key] !== undefined ? r[key] : '';
          } else {
            row[field] = '';
          }
        });
        return row;
      });

      // ─── EXPORT BASED ON FORMAT ───
      if (format === 'pdf') {
        // Generate PDF
        const doc = new jsPDF('l', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        
        doc.setFontSize(18);
        doc.setTextColor(40, 40, 40);
        doc.text('Restaurants Report', pageWidth / 2, 15, { align: 'center' });
        
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Exported: ${new Date().toLocaleString()}`, pageWidth / 2, 22, { align: 'center' });
        
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(`Total Restaurants: ${exportData.length}`, 14, 30);
        
        const headers = Object.keys(exportData[0] || {});
        const rows = exportData.map(item => Object.values(item));

        autoTable(doc, {
          head: [headers],
          body: rows,
          startY: 35,
          styles: { 
            fontSize: 7, 
            cellPadding: 2.5,
            textColor: [50, 50, 50]
          },
          headStyles: { 
            fillColor: [52, 73, 94], 
            textColor: [255, 255, 255],
            fontSize: 8,
            fontStyle: 'bold',
            halign: 'center',
            cellPadding: 2.5
          },
          margin: { left: 10, right: 10 },
          pageBreak: 'auto',
          didDrawPage: function(data) {
            doc.setFontSize(7);
            doc.setTextColor(150, 150, 150);
            doc.text(
              `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
              pageWidth / 2,
              pageHeight - 8,
              { align: 'center' }
            );
          }
        });

        const fileName = `Restaurants_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        showToast(`Exported ${exportData.length} restaurants to PDF!`);
      } else {
        // Generate CSV
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        const colWidths = fields.map(() => ({ wch: 25 }));
        ws['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, 'Restaurants');
        const excelBuffer = XLSX.write(wb, { bookType: 'csv', type: 'array' });
        const blob = new Blob([excelBuffer], { 
          type: 'text/csv;charset=utf-8;' 
        });
        
        const fileName = `Restaurants_${new Date().toISOString().split('T')[0]}.csv`;
        saveAs(blob, fileName);
        showToast(`Exported ${exportData.length} restaurants to CSV!`);
      }
      
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting:', error);
      showToast(`Failed to export: ${error.message}`, "error");
    } finally {
      setExporting(false);
    }
  };

  // ─── EXPORT TO PDF (Quick Export) ───
  const exportToPDF = async (option = 'current') => {
    setExporting(true);
    try {
      let dataToExport;
      let fileNameSuffix;
      
      if (option === 'all') {
        dataToExport = allRestaurants.length > 0 ? allRestaurants : restaurants;
        fileNameSuffix = 'All_Restaurants';
      } else {
        dataToExport = restaurants;
        fileNameSuffix = 'Restaurants_Page_' + page;
      }

      const data = getExportData(dataToExport);
      if (data.length === 0) {
        showToast("No restaurants to export!", "error");
        setExporting(false);
        return;
      }

      const doc = new jsPDF('l', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      const title = option === 'all' ? 'All Restaurants Report' : `Restaurants Report - Page ${page}`;
      doc.text(title, pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Exported: ${new Date().toLocaleString()}`, pageWidth / 2, 22, { align: 'center' });
      
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(`Total Restaurants: ${data.length}`, 14, 30);
      
      const headers = Object.keys(data[0]);
      const rows = data.map(item => Object.values(item));

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 35,
        styles: { 
          fontSize: 7, 
          cellPadding: 2.5,
          textColor: [50, 50, 50]
        },
        headStyles: { 
          fillColor: [52, 73, 94], 
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center',
          cellPadding: 2.5
        },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 22 },
          2: { cellWidth: 18 },
          3: { cellWidth: 15 },
          4: { cellWidth: 20 },
          5: { cellWidth: 12 },
          6: { cellWidth: 12 },
          7: { cellWidth: 12 },
          8: { cellWidth: 12 },
          9: { cellWidth: 18 },
          10: { cellWidth: 18 },
          11: { cellWidth: 25 },
          12: { cellWidth: 18 },
          13: { cellWidth: 15 },
          14: { cellWidth: 15 },
          15: { cellWidth: 15 },
          16: { cellWidth: 12 },
          17: { cellWidth: 15 },
          18: { cellWidth: 18 },
          19: { cellWidth: 18 },
          20: { cellWidth: 15 },
          21: { cellWidth: 18 },
          22: { cellWidth: 25 },
        },
        margin: { left: 10, right: 10 },
        pageBreak: 'auto',
        didDrawPage: function(data) {
          doc.setFontSize(7);
          doc.setTextColor(150, 150, 150);
          doc.text(
            `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
            pageWidth / 2,
            pageHeight - 8,
            { align: 'center' }
          );
        }
      });

      const fileName = `${fileNameSuffix}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      showToast(`Exported ${data.length} restaurants to PDF!`);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showToast('Failed to export PDF. Please try again.', "error");
    } finally {
      setExporting(false);
    }
  };

  // ─── EXPORT TO EXCEL (Quick Export) ───
  const exportToExcel = async (option = 'current') => {
    setExporting(true);
    try {
      let dataToExport;
      let fileNameSuffix;
      
      if (option === 'all') {
        dataToExport = allRestaurants.length > 0 ? allRestaurants : restaurants;
        fileNameSuffix = 'All_Restaurants';
      } else {
        dataToExport = restaurants;
        fileNameSuffix = 'Restaurants_Page_' + page;
      }

      const data = getExportData(dataToExport);
      if (data.length === 0) {
        showToast("No restaurants to export!", "error");
        setExporting(false);
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      
      ws['!cols'] = [
        { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 15 },
        { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 30 },
        { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
        { wch: 15 }, { wch: 18 }, { wch: 25 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Restaurants');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const fileName = `${fileNameSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
      showToast(`Exported ${data.length} restaurants to Excel!`);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showToast('Failed to export Excel. Please try again.', "error");
    } finally {
      setExporting(false);
    }
  };

  // ─── EXPORT SELECTED ───
  const exportSelected = () => {
    if (selected.size === 0) {
      showToast("Please select at least one restaurant to export.", "error");
      return;
    }

    setExporting(true);
    try {
      const selectedRestaurants = restaurants.filter(r => selected.has(r.id));
      const exportData = selectedRestaurants.map(r => ({
        'Restaurant ID': r.restroId || r.id || '',
        'Name': r.organisation_name || r.name || 'N/A',
        'Owner': r.owner_name || 'N/A',
        'Contact': r.contact || 'N/A',
        'Email': r.email || 'N/A',
        'Status': r.status || 'N/A',
        'Ready': r.ready || 'N/A',
        'ON/OFF': r.onOff || 'N/A',
        'Rating': r.rating || 'N/A',
        'City': r.city || 'N/A',
        'Area': r.area || 'N/A',
        'Address': r.address || 'N/A',
        'Cuisine': r.cuisine || 'N/A',
        'Shift Start': r.shiftStart || 'N/A',
        'Shift End': r.shiftEnd || 'N/A',
        'Platform Charge': r.platform_charge || '0',
        'GST %': r.gst_percentage || '0',
        'Wallet': r.wallet || '0',
        'Bank Name': r.bank_name || 'N/A',
        'Account Holder': r.account_holder || 'N/A',
        'IFSC': r.ifsc || 'N/A',
        'GST Number': r.gst_number || 'N/A',
        'Created At': r.created_at ? new Date(r.created_at).toLocaleString() : 'N/A'
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 15 },
        { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 30 },
        { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
        { wch: 15 }, { wch: 18 }, { wch: 25 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, 'Selected Restaurants');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const fileName = `Selected_Restaurants_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
      showToast(`Exported ${exportData.length} selected restaurants!`);
    } catch (error) {
      console.error('Error exporting selected:', error);
      showToast('Failed to export selected restaurants.', "error");
    } finally {
      setExporting(false);
    }
  };

  // ─── HANDLERS ───
  const handleAddRestaurantClick = () => {
    if (!hasPermission('restaurants.create')) {
      showToast("You don't have permission to add restaurants", "error");
      return;
    }
    navigate("/dashboard/restaurants/create", { state: { mode: "add" } });
  };
  
  const handleEditRestaurantClick = (restaurant) => {
    if (!hasPermission('restaurants.edit')) {
      showToast("You don't have permission to edit restaurants", "error");
      return;
    }
    navigate("/Dashboard/editrestaurant", { state: { mode: "edit", restaurantData: restaurant } });
  };
  
  const handleDeleteRestaurantClick = (restaurant) => {
    if (!hasPermission('restaurants.delete')) {
      showToast("You don't have permission to delete restaurants", "error");
      return;
    }
    setShowDeleteModal(restaurant);
  };
  
  const handleViewClick = (restro) => {
    setShowDetailPage(restro);
  };

  // ─── MAPPED RESTAURANTS ───
  const mappedRestaurants = restaurants.map((r, idx) => ({ 
    id: r.id, 
    // Keep one descending serial sequence across all pages: 355, 354, 353...
    // Changing the visible row count must not reset this number.
    sn: Math.max(0, total - ((page - 1) * perPage) - idx),
    restroId: r.restroId,
    old_id: r.old_id,
    status: r.status, 
    ready: r.ready, 
    onOff: r.onOff, 
    shiftStart: r.shiftStart, 
    shiftEnd: r.shiftEnd, 
    name: r.name, 
    organisation_name: r.organisation_name || r.name, 
    owner_name: r.owner_name,
    contact: r.contact || r.owner_mobile, 
    password: r.password, 
    pic: r.pic, 
    schedule: r.schedule, 
    rating: r.rating, 
    totalRatings: r.totalRatings, 
    totalPayments: r.totalPayments, 
    pendingPayments: r.pendingPayments, 
    completedPayments: r.completedPayments, 
    menu: r.menu, 
    address: r.address, 
    email: r.email, 
    description: r.description, 
    cuisine: r.cuisine,
    city: r.city, 
    area: r.area, 
    latitude: r.latitude || "", 
    longitude: r.longitude || "",
    rawData: r.rawData || r,
    organisation_name: r.organisation_name,
    restropic: r.restropic,
    shift_start: r.shift_start,
    shift_end: r.shift_end,
    open_status: r.open_status,
    is_ready: r.is_ready,
    hide: r.hide,
    is_auto_schedule: r.is_auto_schedule,
    is_schedule_approved: r.is_schedule_approved,
    platform_charge: r.platform_charge,
    handling_charge: r.handling_charge,
    gst_percentage: r.gst_percentage,
    platform_share: r.platform_share,
    wallet: r.wallet,
    paid: r.paid,
    bank_name: r.bank_name,
    bank_branch: r.bank_branch,
    bank_type: r.bank_type,
    ifsc_code: r.ifsc_code,
    ifsc: r.ifsc,
    account_holder: r.account_holder,
    account_number: r.account_number,
    bank_mobile: r.bank_mobile,
    upi: r.upi,
    bank_pass: r.bank_pass,
    cancel_cheque: r.cancel_cheque,
    gst_number: r.gst_number,
    gst_pic: r.gst_pic,
    food_license_number: r.food_license_number,
    food_license_pic: r.food_license_pic,
    aadhar_front: r.aadhar_front,
    aadhar_back: r.aadhar_back,
    pan_card: r.pan_card,
    aadhar_number: r.aadhar_number,
    pan_number: r.pan_number,
    is_verify_personalinfo: r.is_verify_personalinfo,
    is_verify_docinfo: r.is_verify_docinfo,
    is_verify_bankinfo: r.is_verify_bankinfo,
    remarks_personalinfo: r.remarks_personalinfo,
    remarks_docinfo: r.remarks_docinfo,
    remarks_bankinfo: r.remarks_bankinfo,
    is_approved: r.is_approved,
    doc_verification: r.doc_verification,
    is_menu: r.is_menu,
    isKOT: r.isKOT,
    isBill: r.isBill,
    menu_direction: r.menu_direction,
    menu_direction_type: r.menu_direction_type,
    menu_direction_value: r.menu_direction_value,
    current_menu_page1: r.current_menu_page1,
    current_menu_page2: r.current_menu_page2,
    current_menu_page3: r.current_menu_page3,
    current_menu_page4: r.current_menu_page4,
    current_menu_page5: r.current_menu_page5,
    current_menu_page6: r.current_menu_page6,
    current_menu_page7: r.current_menu_page7,
    current_menu_page8: r.current_menu_page8,
    current_menu_page9: r.current_menu_page9,
    comission: r.comission,
    special_comission: r.special_comission,
    min_order: r.min_order,
    order_receiving_charges: r.order_receiving_charges,
    subscription_id: r.subscription_id,
    is_mou: r.is_mou,
    myreferal: r.myreferal,
    refer_by: r.refer_by,
    created_by_id: r.created_by_id,
    details: r.details,
    subject: r.subject,
    meet_to: r.meet_to,
    interestedin: r.interestedin,
    interestedin_zaptodoor: r.interestedin_zaptodoor,
    other_person_name: r.other_person_name,
    other_person_mobile: r.other_person_mobile,
    state: r.state,
    postal: r.postal,
    location: r.location,
    r_logo: r.r_logo,
    h_logo: r.h_logo,
    created_at: r.created_at,
    updated_at: r.updated_at,
    phn_otp1: r.phn_otp1,
    phn_otp2: r.phn_otp2,
    login_session: r.login_session,
    refer_code: r.refer_code,
    totalReviews: r.totalReviews,
  }));

  const pageIds = mappedRestaurants.map(r => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(prev => { 
    const next = new Set(prev); 
    if (allPageSelected) pageIds.forEach(id => next.delete(id)); 
    else pageIds.forEach(id => next.add(id)); 
    return next; 
  });
  const toggleOne = (id) => setSelected(prev => { 
    const n = new Set(prev); 
    if (n.has(id)) n.delete(id); else n.add(id); 
    return n; 
  });
  const clearSelection = () => setSelected(new Set());

  // ─── DELETE FUNCTIONS ───
  const deleteRestaurantsByIds = async (restaurantIds) => {
    if (!hasPermission('restaurants.delete')) {
      showToast("You don't have permission to delete restaurants", "error");
      return;
    }

    const ids = restaurantIds
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);

    if (ids.length === 0) {
      showToast("No valid restaurant selected", "error");
      return;
    }

    setDeleting(true);
    try {
      const results = await Promise.all(
        ids.map(async (id) => ({ id, response: await deleteData(`restaurant/Restrunts/${id}`) }))
      );
      const deletedIds = results
        .filter(({ response }) => response?.status === true)
        .map(({ id }) => id);
      const failed = results.filter(({ response }) => response?.status !== true);

      if (deletedIds.length > 0) {
        setSelected((previous) => {
          const next = new Set(previous);
          deletedIds.forEach((id) => next.delete(id));
          return next;
        });
        setShowDeleteModal(null);

        if (showDetailPage && deletedIds.includes(Number(showDetailPage.id))) {
          setShowDetailPage(null);
        }

        await Promise.all([
          fetchRestaurants(),
          fetchAllRestaurants({ force: true })
        ]);
      }

      if (failed.length > 0) {
        const failedIds = failed.map(({ id }) => id).join(', ');
        showToast(`Delete failed for restaurant ID: ${failedIds}`, "error");
      } else {
        showToast(`${deletedIds.length} restaurant(s) deleted successfully`);
      }
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSelected = async () => {
    await deleteRestaurantsByIds(Array.from(selected));
  };

  const handleConfirmDelete = async () => {
    const target = showDeleteModal?.id;
    if (Array.isArray(target)) {
      await handleDeleteSelected();
      return;
    }
    await deleteRestaurantsByIds([target]);
  };
  
  const handleDeleteFromDetail = async (restaurant) => { 
    if (!hasPermission('restaurants.delete')) {
      showToast("You don't have permission to delete restaurants", "error");
      return;
    }
    setShowDeleteModal(restaurant); 
  };

  // ─── SCHEDULE FUNCTIONS ───
  const openScheduleModal = (restaurant) => {
    setScheduleRestaurant(restaurant);
    setScheduleItems(restaurant.schedule && restaurant.schedule.length > 0 ? restaurant.schedule : [
      { day: "Monday", openTime: "09:00", closeTime: "17:00", isClosed: false },
      { day: "Tuesday", openTime: "09:00", closeTime: "17:00", isClosed: false },
      { day: "Wednesday", openTime: "09:00", closeTime: "17:00", isClosed: false },
      { day: "Thursday", openTime: "09:00", closeTime: "17:00", isClosed: false },
      { day: "Friday", openTime: "09:00", closeTime: "17:00", isClosed: false },
      { day: "Saturday", openTime: "09:00", closeTime: "17:00", isClosed: false },
      { day: "Sunday", openTime: "09:00", closeTime: "17:00", isClosed: false }
    ]);
    setShowScheduleModal(true);
  };
  
  const addScheduleItem = (newItem) => {
    if (scheduleItems.some(item => item.day === newItem.day)) {
      showToast("Schedule for this day already exists", "error");
      return;
    }
    setScheduleItems([...scheduleItems, { ...newItem }]);
  };
  
  const updateScheduleItem = (index, updatedItem) => { 
    const updated = [...scheduleItems]; 
    updated[index] = updatedItem; 
    setScheduleItems(updated); 
  };
  
  const deleteScheduleItem = (index) => setScheduleItems(scheduleItems.filter((_, i) => i !== index));
  
  const saveSchedule = async () => {
    try {
      showToast("Opening/Closing times updated successfully");
      setShowScheduleModal(false);
      fetchRestaurants();
      if (showDetailPage && showDetailPage.id === scheduleRestaurant.id) {
        setShowDetailPage({ ...showDetailPage, schedule: scheduleItems });
      }
    } catch (err) {
      showToast(`Failed to save schedule: ${err.message}`, "error");
    }
  };

  const totalPages = Math.ceil(total / perPage);

  // ─── STYLES ───
  const columnWidths = {
    checkbox: "36px",
    sn: "44px",
    id: "60px",
    logo: "48px",
    status: "80px",
    ready: "60px",
    approveStatus: "110px",
    restaurant: "220px",
    contact: "150px",
    actions: "80px"
  };

  const TOTAL_COLUMNS = 10;
  
  const styles = {
    container: { minHeight: "100vh", background: theme.background, fontFamily: "'DM Sans', sans-serif", padding: "20px 24px" },
    headerActions: { display: "flex", gap: "12px", marginBottom: "18px", flexWrap: "wrap", alignItems: "center" },
    addBtn: { 
      background: theme.primary, 
      color: "white", 
      border: "none", 
      padding: "10px 20px", 
      borderRadius: "10px", 
      cursor: "pointer", 
      fontWeight: 600, 
      fontSize: "14px", 
      display: "flex", 
      alignItems: "center", 
      gap: "6px",
      transition: "all 0.3s ease"
    },
    exportBtn: { 
      display: "flex", 
      alignItems: "center", 
      gap: "6px", 
      padding: "10px 16px", 
      borderRadius: "10px", 
      border: `1px solid ${theme.border}`, 
      background: theme.surface, 
      color: theme.textPrimary, 
      cursor: "pointer", 
      fontSize: "13px", 
      fontWeight: 500,
      transition: "all 0.3s ease",
      whiteSpace: "nowrap"
    },
    filtersBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px", flexWrap: "wrap", gap: "14px" },
    searchWrapper: { position: "relative", flex: 1, maxWidth: "320px" },
    searchInput: { width: "100%", padding: "10px 12px 10px 36px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "10px", fontSize: "13px", color: theme.textPrimary, outline: "none" },
    searchIcon: { position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: theme.primary },
    filterControls: { display: "flex", gap: "18px", alignItems: "center", flexWrap: "wrap" },
    filterGroup: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: theme.textSecondary },
    select: { padding: "6px 10px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "6px", color: theme.textPrimary, cursor: "pointer", fontSize: "12px" },
    showHidden: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: theme.textSecondary },
    selectionBar: { display: "flex", alignItems: "center", gap: "12px", background: `${theme.error}1a`, border: `1px solid ${theme.error}40`, borderRadius: "8px", padding: "8px 16px", marginBottom: "12px", flexWrap: "wrap" },
    tableWrapper: { background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "12px", overflow: "hidden", width: "100%" },
    table: { width: "100%", borderCollapse: "collapse", tableLayout: "fixed" },
    th: (width) => ({ 
      padding: "12px 8px", 
      textAlign: "left", 
      fontSize: "12px", 
      fontWeight: 700, 
      color: theme.primary, 
      borderBottom: `1px solid ${theme.border}`, 
      background: theme.primaryLight, 
      width: width || "auto", 
      whiteSpace: "nowrap",
      letterSpacing: "0.3px"
    }),
    td: { 
      padding: "10px 8px", 
      borderBottom: `1px solid ${theme.border}`, 
      fontSize: "13px", 
      color: theme.textPrimary, 
      whiteSpace: "nowrap", 
      overflow: "hidden", 
      textOverflow: "ellipsis"
    },
    pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: `1px solid ${theme.border}`, background: theme.primaryLight, flexWrap: "wrap", gap: "10px" },
    viewMoreBtn: { background: theme.primary, color: "white", border: "none", padding: "5px 12px", borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap" },
    restaurantName: { 
      fontWeight: 700, 
      fontSize: "13px", 
      maxWidth: "230px", 
      overflow: "hidden", 
      textOverflow: "ellipsis", 
      whiteSpace: "nowrap", 
      display: "inline-block",
      color: theme.textPrimary
    },
    contactWrapper: { 
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "13px", 
      whiteSpace: "nowrap", 
      color: theme.textPrimary,
      fontWeight: 600,
    },
    contactIcon: {
      color: theme.primary,
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
    },
    idText: { 
      fontFamily: "monospace", 
      fontSize: "11px", 
      color: theme.primary,
      fontWeight: 600
    }
  };

  // ─── RENDER DETAIL PAGE ───
  if (showDetailPage) {
    return (
      <RestaurantDetailPage
        restaurant={showDetailPage}
        onBack={() => setShowDetailPage(null)}
        onEdit={(restaurant) => handleEditRestaurantClick(restaurant)}
        onDelete={(restaurant) => handleDeleteFromDetail(restaurant)}
        onViewSchedule={(restaurant) => openScheduleModal(restaurant)}
        onViewMenu={(restaurant) => setShowMenuModal(restaurant)}
        isDark={isDark}
      />
    );
  }

  // ─── MAIN RENDER ───
  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideUp { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @media (max-width: 1200px) { body { font-size: 11px; } }
      `}</style>

      <div style={styles.headerActions}>
        <PermissionGuard permission="restaurants.create" fallback={null}>
          <button style={styles.addBtn} onClick={handleAddRestaurantClick}>
            <PlusIcon /> Add Restaurant
          </button>
        </PermissionGuard>

        {/* Total Count Display */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: theme.textSecondary, background: theme.surface, padding: "6px 14px", borderRadius: "8px", border: `1px solid ${theme.border}` }}>
          <span style={{ fontWeight: 600 }}>Total:</span>
          <span style={{ fontWeight: 700, color: theme.primary }}>{allRestaurants.length || total}</span>
          {locationFilter && locationFilter !== "All" && (
            <>
              <span style={{ color: theme.textMuted }}>|</span>
              <span style={{ fontWeight: 600 }}>In {locationFilter}:</span>
              <span style={{ fontWeight: 700, color: theme.success }}>{locationStats[locationFilter] || 0}</span>
            </>
          )}
        </div>

        {/* Export Modal Button */}
        <button 
          style={{...styles.exportBtn, background: theme.success, color: '#fff', borderColor: theme.success}}
          onClick={() => setShowExportModal(true)}
          disabled={exporting || restaurants.length === 0}
        >
          <FileSpreadsheetIcon /> Export
        </button>
        
        {/* Quick Export Dropdown */}
        <DropdownMenu
          theme={theme}
          trigger={
            <button 
              style={styles.exportBtn}
              disabled={exporting || restaurants.length === 0}
            >
              <DownloadIcon /> {exporting ? 'Exporting...' : 'Quick Export'} <ChevronDownIcon />
            </button>
          }
        >
          <DropdownItem 
            onClick={() => exportToPDF('current')} 
            theme={theme}
            icon={<PDFIcon />}
          >
            PDF - Current Page ({restaurants.length})
          </DropdownItem>
          <DropdownItem 
            onClick={() => exportToPDF('all')} 
            theme={theme}
            icon={<PDFIcon />}
          >
            PDF - All Data ({allRestaurants.length || total})
          </DropdownItem>
          <div style={{ borderTop: `1px solid ${theme.border}` }} />
          <DropdownItem 
            onClick={() => exportToExcel('current')} 
            theme={theme}
            icon={<ExcelIcon />}
          >
            Excel - Current Page ({restaurants.length})
          </DropdownItem>
          <DropdownItem 
            onClick={() => exportToExcel('all')} 
            theme={theme}
            icon={<ExcelIcon />}
          >
            Excel - All Data ({allRestaurants.length || total})
          </DropdownItem>
          {selected.size > 0 && (
            <>
              <div style={{ borderTop: `1px solid ${theme.border}` }} />
              <DropdownItem 
                onClick={exportSelected} 
                theme={theme}
                icon={<DownloadIcon />}
              >
                Export Selected ({selected.size})
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </div>

      <div style={styles.filtersBar}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}><SearchIcon /></span>
          <input type="text" style={styles.searchInput} placeholder="Search restaurant, address, city or area..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div style={styles.filterControls}>
          <label style={styles.showHidden}>
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(event) => {
                setShowHidden(event.target.checked);
                setPage(1);
                clearSelection();
              }}
            /> Show Hidden
          </label>
          <div style={styles.filterGroup}>
            <label>Status:</label>
            <select style={styles.select} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option>All</option><option>Active</option><option>Inactive</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label>Ready:</label>
            <select style={styles.select} value={readyFilter} onChange={(e) => { setReadyFilter(e.target.value); setPage(1); }}>
              <option>All</option><option>Yes</option><option>No</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label>Approve:</label>
            <select style={styles.select} value={approveStatusFilter} onChange={(e) => { setApproveStatusFilter(e.target.value); setPage(1); }}>
              <option>All</option><option>Approved</option><option>Pending</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label>City:</label>
            <select style={styles.select} value={locationFilter} onChange={(e) => { setLocationFilter(e.target.value); setPage(1); }}>
              {uniqueLocations.map((city) => <option key={city} value={city}>{city}</option>)}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label>ON/OFF:</label>
            <select style={styles.select} value={onOffFilter} onChange={(e) => { setOnOffFilter(e.target.value); setPage(1); }}>
              <option>All</option><option>ON</option><option>OFF</option>
            </select>
          </div>
        </div>
      </div>

      {selected.size > 0 && (
        <div style={styles.selectionBar}>
          <span style={{ fontSize: "13px", color: theme.error, fontWeight: 600 }}>✓ {selected.size} selected</span>
          <div style={{ flex: 1 }} />
          <button onClick={clearSelection} style={{ padding: "4px 12px", borderRadius: "6px", border: `1px solid ${theme.border}`, background: theme.surface, color: theme.textSecondary, fontSize: "12px", cursor: "pointer" }}>Clear</button>
          <PermissionGuard permission="restaurants.delete" fallback={null}>
            <button onClick={() => setShowDeleteModal({ id: Array.from(selected), name: `${selected.size} restaurants` })} style={{ padding: "4px 12px", borderRadius: "6px", border: `1px solid ${theme.error}30`, background: `${theme.error}1a`, color: theme.error, fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}><TrashIcon /> Delete</button>
          </PermissionGuard>
        </div>
      )}

      <div style={styles.tableWrapper}>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th(columnWidths.checkbox)}><Checkbox checked={allPageSelected} onChange={toggleAll} theme={theme} /></th>
                <th style={styles.th(columnWidths.sn)}>#</th>
                <th style={styles.th(columnWidths.id)}>ID</th>
                <th style={styles.th(columnWidths.logo)}>Logo</th>
                <th style={styles.th(columnWidths.status)}>Status</th>
                <th style={styles.th(columnWidths.ready)}>Ready</th>
                <th style={styles.th(columnWidths.approveStatus)}>Approve Status</th>
                <th style={styles.th(columnWidths.restaurant)}>Restaurant</th>
                <th style={styles.th(columnWidths.contact)}>Contact</th>
                <th style={styles.th(columnWidths.actions)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: perPage }).map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    {Array.from({ length: TOTAL_COLUMNS }).map((_, j) => (
                      <td key={`skeleton-cell-${i}-${j}`} style={styles.td}>
                        <div style={{ height: "12px", background: theme.border, borderRadius: "4px", width: j === 0 ? "14px" : "70%" }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : mappedRestaurants.length === 0 ? (
                <tr>
                  <td colSpan={TOTAL_COLUMNS} style={{ padding: "50px", textAlign: "center", color: theme.textMuted, fontSize: "14px" }}>
                    {debouncedSearch ? "No results found" : "No restaurants found"}
                  </td>
                </tr>
              ) : (
                mappedRestaurants.map((restro) => (
                  <tr key={restro.id} style={{ background: selected.has(restro.id) ? theme.primaryLight : "transparent" }}>
                    <td style={styles.td}><Checkbox checked={selected.has(restro.id)} onChange={() => toggleOne(restro.id)} theme={theme} /></td>
                    <td style={styles.td}>{restro.sn}</td>
                    <td style={styles.td}><span style={styles.idText}>{restro.restroId}</span></td>
                    <td style={styles.td}>
                      <RestaurantAvatar pic={restro.pic} name={restro.organisation_name || restro.name} theme={theme} />
                    </td>
                    <td style={styles.td}><StatusBadge status={restro.status} theme={theme} /></td>
                    <td style={styles.td}><ReadyBadge ready={restro.ready} theme={theme} /></td>
                    <td style={styles.td}><ApproveStatusBadge isApproved={restro.is_approved} theme={theme} /></td>
                    <td style={styles.td}>
                      <span style={styles.restaurantName} title={restro.organisation_name || restro.name}>
                        {restro.organisation_name || restro.name || "N/A"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.contactWrapper} title={restro.contact}>
                        <span style={styles.contactIcon}>
                          <PhoneIcon />
                        </span>
                        {restro.contact || "-"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <PermissionGuard permission="restaurants.overview" fallback={null}>
                        <button style={styles.viewMoreBtn} onClick={() => handleViewClick(restro)}>View</button>
                      </PermissionGuard>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.pagination}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "12px", color: theme.textMuted }}>
              {loading ? "Loading..." : `${Math.min((page-1)*perPage+1, total)}–${Math.min(page*perPage, total)} of ${total}`}
            </span>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: theme.textMuted }}>
              Rows:
              <select value={perPage} onChange={handlePerPageChange} disabled={loading} style={{ padding: "5px 8px", borderRadius: "6px", border: `1px solid ${theme.border}`, background: theme.surface, color: theme.textPrimary, fontSize: "12px", cursor: "pointer" }}>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={50}>50</option>
              </select>
            </label>
          </div>
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1 || loading} style={{ width: "28px", height: "28px", borderRadius: "6px", border: `1px solid ${theme.border}`, background: theme.surface, color: page === 1 ? theme.textMuted : theme.textSecondary, cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeftIcon /></button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => { 
              let p; 
              if (totalPages <= 5) p = i + 1;
              else if (page <= 3) p = i + 1;
              else if (page >= totalPages - 2) p = totalPages - 4 + i;
              else p = page - 2 + i;
              return p <= totalPages && (
                <button key={p} onClick={() => setPage(p)} style={{ width: "28px", height: "28px", borderRadius: "6px", fontSize: "12px", fontWeight: page === p ? 700 : 400, border: page === p ? `2px solid ${theme.primary}` : `1px solid ${theme.border}`, background: page === p ? theme.primaryLight : theme.surface, color: page === p ? theme.primary : theme.textSecondary, cursor: "pointer" }}>{p}</button>
              ); 
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages || loading} style={{ width: "28px", height: "28px", borderRadius: "6px", border: `1px solid ${theme.border}`, background: theme.surface, color: page === totalPages ? theme.textMuted : theme.textSecondary, cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronRightIcon /></button>
          </div>
        </div>
      </div>

      <DeleteConfirmModal 
        isOpen={!!showDeleteModal} 
        restaurantName={showDeleteModal?.name || showDeleteModal?.organisation_name || ""} 
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteModal(null)} 
        deleting={deleting}
        theme={theme} 
      />
      
      <ScheduleModal 
        isOpen={showScheduleModal} 
        restaurant={scheduleRestaurant} 
        scheduleItems={scheduleItems} 
        onUpdate={updateScheduleItem} 
        onDelete={deleteScheduleItem} 
        onAdd={addScheduleItem} 
        onClose={() => { setShowScheduleModal(false); setScheduleRestaurant(null); }} 
        onSave={saveSchedule} 
        theme={theme} 
      />
      
      {showMenuModal && <RestaurantMenuModal restaurant={showMenuModal} onClose={() => setShowMenuModal(null)} theme={theme} />}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} theme={theme} />}

      {/* Export Modal */}
      <ExportRestaurantModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        totalRestaurants={allRestaurants.length || total}
        theme={theme}
      />
    </div>
  );
};

export default RestaurantTable;
